import type { ComponentPropsWithRef, ElementType, ReactNode } from "react";
import { cn } from "@/lib/utils";

export type TruncatedTextEllipsis = "end" | "start";

/** CSS classes for single-line overflow ellipsis. `start` keeps the trailing characters. */
export function truncatedTextClassName(
  ellipsis: TruncatedTextEllipsis = "end",
): string {
  return ellipsis === "start"
    ? "min-w-0 overflow-hidden text-ellipsis whitespace-nowrap text-left [direction:rtl]"
    : "min-w-0 truncate";
}

/** Isolate LTR content so start-ellipsis does not reverse character order. */
export function truncatedTextContent(
  children: ReactNode,
  ellipsis: TruncatedTextEllipsis = "end",
): ReactNode {
  return ellipsis === "start" ? <bdi>{children}</bdi> : children;
}

export type TruncatedTextProps<T extends ElementType = "span"> = {
  children: string;
  as?: T;
  title?: string;
  /** Where the ellipsis appears on overflow. `start` shows the last characters. */
  ellipsis?: TruncatedTextEllipsis;
} & Omit<ComponentPropsWithRef<T>, "children" | "title" | "as">;

/**
 * Single-line ellipsis with native tooltip for the full string.
 * For table cells that should wrap instead, use DataTablePrimaryText.
 */
export function TruncatedText<T extends ElementType = "span">({
  children,
  className,
  as: Tag = "span" as T,
  title,
  ellipsis = "end",
  ...rest
}: TruncatedTextProps<T>) {
  const Component = Tag as ElementType;
  return (
    <Component
      className={cn(truncatedTextClassName(ellipsis), className)}
      title={title ?? children}
      {...rest}
    >
      {truncatedTextContent(children, ellipsis)}
    </Component>
  );
}
